import TimeSheet from "molecules/TimeSheet";
import React, { useEffect, useRef, useState } from "react";
import Button from "atoms/Button";
import { DatePicker, Form, Popover, Spin, notification } from "antd";
import { useFetch } from "hooks/useFetch";
import queryKeys from "common/queryKey";
import { ClickAwayListener } from "@mui/material";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getActualSummaryData,
  getCachedTimesheets,
  getHigherOfficials,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { useMutate } from "hooks/useMutate";
import { useUpdate } from "hooks/useUpdate";
import { useQueryClient } from "react-query";
import useWeekData, {
  generateTimesheetWeek,
  periodToDate,
} from "./useWeekData";
import { ConfigProvider } from "antd";
import en_GB from "antd/lib/locale-provider/en_GB";
import moment from "moment";
import "moment/locale/en-gb";
import { apiInstance } from "config/baseUrl";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import TimeSheetOverView from "./TimeSheetOverView";
import { InstructionsTrigger } from "./TimeSheetFooterHint";
import { ONGOING, PENDING, WITHDRAWN, DRAFT } from "./constants";
import { CheckCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import styled from "styled-components";
import useTimesheetfns from "./useTimesheetfns";
import { toggleProcessingModal } from "redux/actions";
import { CALL_API } from "common/API";
import { getClientid, getUserRoleId } from "redux/selectors";
moment.locale("en-gb");

const StyledButtonCancel = styled(Button)`
  background: #ffffff !important;
  border-radius: 5px;
  color: #00685F!important;
  width: 100px;
  border: none;
  &:focus {
      color: #00685F!important;
  }
`;
const StyledButtonBack = styled(Button)`
  background: #ffffff !important;
  border-radius: 5px;
  color: #000000!important;
  border-color: #000000!important;
  &:focus {
      color: #000000 !important;
  }
`;

// `onVisibilityChange` toggles the dark backdrop (shared with the weekly
// summary popover) for the duration the toast is on screen. We flip it on
// right away and flip it off again once the notification's own onClose
// fires (duration 2s, or the caller dismisses it).
const showTimesheetToast = (text, onClose, onVisibilityChange) => {
  onVisibilityChange?.(true);
  notification.success({
    message: (
      <div className="ts-toast">
        {/* <CheckCircleFilled className="ts-toast-icon" /> */}
        <div className="ts-toast-text">{text}</div>
      </div>
    ),
    icon: <CheckCircleFilled className="ts-toast-icon" />,
    closeIcon: <></>,
    className: "timesheet-toast",
    placement: "top",
    duration: 2,
    style: { left: "50%", transform: "translateX(-50%)", top: "40vh", },
    onClose: () => {
      onVisibilityChange?.(false);
      onClose?.();
    },
  });
};

const MyTimeSheet = ({
  selectedTabId,
  selectedPeriod,
  duplicateTimesheet = false,
  actualSummaryData,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const dispatch = useDispatch();
  const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
  const CEO = higherOfficialsList?.[0]?.useremails || "";

  const selectedPeriodToDate = periodToDate(selectedPeriod);

  const [copyWeekTimesheet, setCopyWeekTimesheet] = React.useState(null);
  const [isCopying, setIsCopying] = React.useState(false);
  const [loadingActualSummary, setLoadingActualSummary] = useState(false);

  const footerAnchorRef = useRef(null);
  const [footerLeft, setFooterLeft] = useState(0);
  const [footerRight, setFooterRight] = useState(0);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isToastVisible, setIsToastVisible] = useState(false);

  useEffect(() => {
    // The anchor sits inside the page's normal 24px content padding, but the
    // footer bar should bleed flush to the sidebar/viewport edges (matching
    // WeekTotalsBar's edge-to-edge treatment), so we subtract that padding.
    const CONTENT_PADDING = 24;

    const updateFooterPosition = () => {
      if (footerAnchorRef.current) {
        const rect = footerAnchorRef.current.getBoundingClientRect();
        setFooterLeft(rect.left - CONTENT_PADDING);
        setFooterRight(window.innerWidth - rect.right - CONTENT_PADDING);
      }
    };

    updateFooterPosition();
    window.addEventListener("resize", updateFooterPosition);
    const observer = new ResizeObserver(updateFooterPosition);
    if (footerAnchorRef.current?.parentElement) {
      observer.observe(footerAnchorRef.current.parentElement);
    }

    return () => {
      window.removeEventListener("resize", updateFooterPosition);
      observer.disconnect();
    };
  }, []);

  const {
    date,
    week,
    currentWeek,
    edit,
    setEdit,
    currentNiceWeekRange,
    disabledDate,
    onChange,
    setPersonInCharge,
    personIncharge,
  } = useWeekData(selectedPeriodToDate);

  const { getTimesheet, postTimesheet, updateTimesheet } = queryKeys;

  const userList = useSelector(getUserList);

  const userInfo = useSelector((state) => getUserInfo(state));

  const cachedTimesheets = useSelector(getCachedTimesheets);

  const cachedTimeSheet = cachedTimesheets?.[week] || []; // get cached timesheet for the week

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const OfficeSuperVisorId = currentUser?.OfficeSupervisorId || null;

  const currentUserEmployeeId = userList?.find(
    (user) => user?.userid === currentUser?.userid
  )?.employeeid;

  const OfficeSuperVisorUserId = userList?.find(
    (user) =>
      user.employeeid ===
      (currentUser?.userid === CEO
        ? currentUserEmployeeId
        : OfficeSuperVisorId)
  )?.userid;

  const chargeOutRate = currentUser?.chargeoutrate
    ? parseInt(currentUser?.chargeoutrate)
    : 0;

  const {
    data: timesheetDataRes,
    isLoading,
    isFetching,
    isFetched,
  } = useFetch(
    `${getTimesheet.key}-week:${week}`,
    `${getTimesheet.url}?week=${week}&employeeId=${userInfo.employer_id}`,
    {
      enabled: !!week,
    }
  );

  const clientId = useSelector(getClientid);
  const userRoleId = useSelector(getUserRoleId);

  // Calculate week start and end dates
  const weekStart = moment(currentWeek, "YYYYMMMDD");
  const weekEnd = moment(currentWeek, "YYYYMMMDD").add(6, "days");

  // Fetch holidays
  const { key: holidayKey, url: holidayUrl } = queryKeys.getHolidayList(clientId);
  const { data: holidayDataRes } = useFetch(
    [holidayKey, weekStart, weekEnd],
    holidayUrl,
    { enabled: !!clientId },
    { start_date: weekStart, end_date: weekEnd }
  );

  // Fetch leaves
  const { key: leaveKey, url: leaveUrl } = queryKeys.getLeaveList(
    clientId,
    userInfo?.userid
  );
  const leaveParams = {
    status_id: "",
    officesupervisorid: [""],
    selected_role_id: userRoleId,
    start_date: weekStart,
    end_date: weekEnd,
  };
  const { data: leaveDataRes } = useFetch(
    [leaveKey, weekStart, weekEnd],
    leaveUrl,
    { enabled: !!clientId },
    leaveParams
  );

  // Filter holidays and leaves
  const holidays = holidayDataRes?.response?.filter((holiday) =>
    holiday?.location_id?.includes(currentUser?.baselocationid)
  );

  const leaves =
    leaveDataRes?.response?.leaves?.filter(
      (leave) =>
        leave.userid === userInfo.userid &&
        leave?.status_id === "5" &&
        leave?.having_cancel_request === "0"
    ) || [];


  const hasHolidayOrLeave = holidays?.length > 0 || leaves?.length > 0;
  const isDraft = timesheetDataRes?.response?.response?.status === "draft";

  let apentry = {
    "thu": 0,
    "ncClient": null,
    "engagement": "ap",
    "actual_to_date": "",
    "tue": 0,
    "approval": {},
    "sat": 0,
    "sum": 0,
    "mon": 0,
    "sun": 0,
    "task": "",
    "declined": {},
    "wed": 0,
    "claims": [],
    "subEngagement": null,
    "client": "ap",
    "currentWeek": "",
    "fri": 0,
    "remarks": "",
    "personInCharge": "",
    "budget": "",
    "status": "approved"
  }
  useEffect(() => {
    ;
    if (
      (isDraft && hasHolidayOrLeave) &&
      timesheetDataRes?.response?.response?.timeSheet &&
      !timesheetDataRes?.response?.response?.timeSheet.some(entry => entry.client === "ap")
    ) {
      timesheetDataRes?.response?.response?.timeSheet.push(apentry);
    }
  }, [isDraft, timesheetDataRes]);

  const timeSheetStatus =
    timesheetDataRes?.response?.response?.status || ONGOING;

  const updateableTimesheet =
    timeSheetStatus === WITHDRAWN || timeSheetStatus === DRAFT;

  const [form] = Form.useForm();
  const liveTimeSheetValues = Form.useWatch([], form);

  const { mutate: submitTimesheet, isLoading: isSubmitting } = useMutate(
    postTimesheet.key,
    postTimesheet.url
  );

  const { mutate: update, isLoading: isUpdating } = useUpdate(
    updateTimesheet.key,
    updateTimesheet.url
  );

  const { mutate: resubmitDeclinedMutate, isLoading: isResubmitting } =
    useUpdate(postTimesheet.key, postTimesheet.url);

  const getActualSummary = async (matterId, userId) => {
    try {
      const { data } = await CALL_API(
        `/insyts/timesheet/actualsummary?matterId=${matterId}&userId=${userId}`,
        "get"
      );

      return data;
    } catch (error) {
      console.error("Failed to fetch actualToDate:", error);
      return null;
    }
  };

  const onSubmit = async (
    values,
    draft = false,
    skipActualSummary = false,
    originalTimeSheet
  ) => {
    let timeSheetData = generateTimeSheetData(values, draft);
    if (originalTimeSheet) {
      timeSheetData = generateTimeSheetData(
        values,
        false,
        originalTimeSheet,
        true,
        false,
        true
      );
    }
    if (!draft && !isChargeAbleAmtValid(timeSheetData)) {
      return false;
    }
    const data = {
      week: week,
      employeeId: userInfo.userid,
      employeeName: userInfo.name,
      departementId: userInfo.department,
      rate: chargeOutRate,
      timeSheet: timeSheetData,
      status: draft ? DRAFT : PENDING,
      country: country,
      // totalChargeableHours: filteredResponse,
      timesheetSubmittedDate: draft
        ? null
        : timesheetDataRes?.response?.response?.timesheetSubmittedDate ||
        moment().format("DD-MM-YYYY HH:mm:ss"),
    };
    data.timeSheet = data.timeSheet.map((entry) => {
      const currentStatus = entry?.status?.toLowerCase?.();
      // ✅ SAVE AS DRAFT logic
      if (draft) {
        return {
          ...entry,
          status: currentStatus === "withdrawn" ? "draft" : entry.status,
          isNew: typeof entry.isNew === "boolean" ? entry.isNew : true,
        };
      }

      // ✅ SUBMIT logic
      let newStatus = entry.status;

      if (currentStatus === "withdrawn" || currentStatus === "draft") {
        newStatus = "pending";
      }
      // keep "approved" and "declined" as-is

      return {
        ...entry,
        status: newStatus,
        isNew: typeof entry.isNew === "boolean" ? entry.isNew : false,
      };
    });
    if (!draft) {
      const missingTask = timeSheetData.some((entry) => {
        const currentStatus = entry?.status?.toLowerCase?.();
        const isApproved = currentStatus === "approved";
        const isExisting = !!entry._id || !!entry.id; // adjust based on your DB field

        // Skip validation for approved or existing (legacy) entries
        if (isApproved || isExisting) return false;

        // Require task for new or editable entries
        return !entry.task || entry.task === undefined || entry.task === "";
      });

      if (missingTask) {
        notification.warning({
          message: "Task selection required for editable timesheets while submitting",
        });
        return;
      }
    }
    const existingSubmittedDate = timesheetDataRes?.response?.response?.timesheetSubmittedDate;
    if (updateableTimesheet) {
      const updatedData = {
        ...timesheetDataRes?.response?.response,
        status: draft ? DRAFT : PENDING,
        timeSheet: data.timeSheet,
        ...(!draft && !existingSubmittedDate ? { timesheetSubmittedDate: moment().format("DD-MM-YYYY HH:mm:ss") } : {}),
      };
      update(updatedData, {
        onSuccess: (res) => {
          queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
          queryClient.invalidateQueries(
            `${getTimesheet.key}-week:${week}-status:${draft ? "draft" : "pending"
            }`
          );
          showTimesheetToast(
            draft
              ? "Saved as Draft"
              : updateableTimesheet
                ? "Updated"
                : "Submitted",
            onCancel,
            setIsToastVisible
          );
        },
      });
    } else {
      submitTimesheet(data, {
        onSuccess: (res) => {
          queryClient.invalidateQueries(
            `${getTimesheet.key}-week:${week}-status:${draft ? "draft" : "pending"
            }`
          );
          queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
          showTimesheetToast(draft ? "Saved as Draft" : "Submitted", onCancel, setIsToastVisible);
        },
      });
    }
    if (!skipActualSummary) {
      setLoadingActualSummary(true);
      try {
        const payloads = [];
        const groupedByEngagement = {};

        values.forEach((item) => {
          const matterId = item.engagement;
          if (!groupedByEngagement[matterId]) {
            groupedByEngagement[matterId] = [];
          }
          groupedByEngagement[matterId].push(item);
        });

        // Process each matterId sequentially using for...of
        for (const [matterId, items] of Object.entries(
          groupedByEngagement
        )) {
          const resourceId = items[0].resourceId;
          const status = items[0].status || "pending";

          const actualSummaryData = await getActualSummary(
            matterId,
            userInfo.userid
          );

          const hasExisting =
            (actualSummaryData?.actualTimesheet || []).length > 0;

          const existingEntries = hasExisting
            ? actualSummaryData.actualTimesheet.filter(
              (entry) =>
                entry.resourceId === resourceId && entry.week !== week
            )
            : [];

          const payload = {
            matterId,
            userId: userInfo.userid,
            actualTimesheet: [...existingEntries],
          };

          items.forEach((item) => {
            payload.actualTimesheet.push({
              resourceId: item.resourceId,
              week: week,
              sum: item.sum || 0,
              actualhours: item.actual_to_date,
              status: item.status || "pending",
            });
          });

          payloads.push({
            method: hasExisting ? "patch" : "post",
            data: payload,
          });
        }

        // Send all API requests in parallel
        const responses = await Promise.all(
          payloads.map(({ method, data }) =>
            CALL_API("insyts/timesheet/actualsummary", method, data)
          )
        );
      } catch (error) {
        console.error("Error submitting actual summaries:", error);
      } finally {
        setLoadingActualSummary(false);
      }
    }
  };

  useEffect(() => {
    dispatch(toggleProcessingModal(isUpdating || isSubmitting));
  }, [isUpdating, isSubmitting]);

  const {
    timeSheet,
    setTimeSheet,
    generateTimeSheetData,
    handleAddMore,
    handleAddMoreClaim,
    handleClearClaim,
    handleResetClaim,
    handleRemoveClaim,
    handlePersonInCharge,
    handleClearTimesheet,
    handleSaveTimesheet,
    isChargeAbleAmtValid,
    handleSubmitTimesheet,
  } = useTimesheetfns({
    personIncharge,
    OfficeSuperVisorUserId,
    currentWeek,
    form,
    setPersonInCharge,
    week,
    timesheetDataRes,
    onSubmit,
    isFetched,
    duplicateTimesheet,
  });

  const editableTimesheet =
    timeSheetStatus === WITHDRAWN ||
    timeSheetStatus === ONGOING ||
    timeSheetStatus === DRAFT;

  const handleWithdrawTimesheet = (rowKey) => {
    const currentData = timesheetDataRes?.response?.response;
    if (!currentData || Array.isArray(currentData) || !currentData?.timeSheet) {
      notification.error({
        message: "This timesheet hasn't been submitted yet, so it can't be withdrawn",
      });
      return;
    }

    const values = form.getFieldsValue(true);
    const liveTimeSheet = generateTimeSheetData(values, false, currentData.timeSheet);

    const data = {
      ...currentData,
      timeSheet: liveTimeSheet.map((sec, index) => {
        if (index !== rowKey) {
          return sec;
        }
        return { ...sec, status: WITHDRAWN };
      }),
      status: WITHDRAWN,
    };

    update(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
        queryClient.invalidateQueries(
          `${getTimesheet.key}-week:${week}-status:pending`
        );
        notification.success({
          message: "Timesheet Withdrawn Successfully",
        });
      },
    });
  };

  const handleResubmitDeclined = (rowKey, onDone) => {
    const currentData = timesheetDataRes?.response?.response;
    if (!currentData) return;

    const values = form.getFieldsValue(true);
    const liveTimeSheet = generateTimeSheetData(
      values,
      false,
      currentData.timeSheet,
      true,
      false,
      false,
      true
    );

    if (!isChargeAbleAmtValid(liveTimeSheet)) {
      return;
    }

    const updatedTimeSheet = currentData.timeSheet.map((sec, index) => {
      if (index !== rowKey) {
        return sec;
      }
      return { ...liveTimeSheet[index], status: PENDING, declined: {} };
    });

    const updatedData = {
      ...currentData,
      status: PENDING,
      timeSheet: updatedTimeSheet,
    };

    resubmitDeclinedMutate(updatedData, {
      onSuccess: () => {
        queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
        showTimesheetToast("Submitted", undefined, setIsToastVisible);
        onDone?.();
      },
    });
  };

  useEffect(() => {
    if (
      cachedTimeSheet.length > 0 &&
      !timesheetDataRes?.response?.response?.timeSheet
    ) {
      setTimeSheet(cachedTimeSheet);
      form.setFieldsValue(cachedTimeSheet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedTimeSheet, timesheetDataRes]);

  const pasteTimeSheet = async (week, date) => {
    setIsCopying(true);
    const accessToken = window.localStorage.getItem("token");

    try {
      const response = await queryClient.fetchQuery({
        queryKey: `${getTimesheet.key}-week:${week}`,
        queryFn: async () => {
          return await apiInstance({
            method: "GET",
            url: `${getTimesheet.url}?week=${week}&employeeId=${userInfo.employer_id}`,
            headers: {
              Insytsjwt: accessToken,
            },
          })
            .then((res) => {
              return res.data;
            })
            .catch((err) => {
              return err.response;
            });
        },
      });

      const timeSheetResponse =
        response?.response?.response?.timeSheet || false;

      if (timeSheetResponse) {
        // onChange(date);

        const statusRemovedTimesheet = timeSheetResponse.map((value) => {
          return {
            ...value,
            status: "",
            approval: {},
            declined: {},
          };
        });

        setTimeSheet(statusRemovedTimesheet);
        form.setFieldsValue(statusRemovedTimesheet);
      } else {
        notification.error({
          // message: `Timesheet for week ${week} not found`,
          message: `Timesheet Not Found`,
        });
      }
      setCopyWeekTimesheet(null);
      setIsCopying(false);
    } catch (error) {
      setIsCopying(false);
      console.error("error", error);
    }
  };

  const handlePasteTimesheet = (date) => {
    var weekStart = date.clone().startOf("isoWeek");
    var weekEnd = date.clone().endOf("isoWeek");

    setCopyWeekTimesheet(date);
    pasteTimeSheet(generateTimesheetWeek(weekStart, weekEnd), weekStart);
  };

  const weekNavNode = (
    <div className="wn-date-nav">
      {edit ? (
        <ClickAwayListener onClickAway={() => setEdit(false)}>
          <div className="position-relative">
            <ConfigProvider locale={en_GB}>
              <DatePicker
                onChange={onChange}
                picker="week"
                value={date}
                open={true}
                disabledDate={disabledDate}
                allowClear={false}
              />
            </ConfigProvider>
          </div>
        </ClickAwayListener>
      ) : (
        <div className="wn-current-week" onClick={() => setEdit(true)}>
          {currentNiceWeekRange}
        </div>
      )}
    </div>
  );

  return (
    <Form
      form={form}
      initialValues={timeSheet}
      layout="vertical"
      onFinishFailed={() => {
        notification.error({
          message: "Please fill all the required fields",
        });
      }}
    >
      <style>
        {`
      .add-more,
      .add-more:hover,
      .add-more:focus {
        background: #ffffff !important;
        color: #1f243b !important;
        border-color: #1f243b !important;
        border-radius: 8px !important;
        font-family: 'DM Sans', sans-serif;
      }

      .submit-button,
      .submit-button:hover,
      .submit-button:focus {
        background: #00685F !important;
        color: #ffffff !important;
        border-color: #00685F !important;
        border-radius: 8px !important;
        font-family: 'DM Sans', sans-serif;
      }

      .cancel-button,
      .cancel-button:hover,
      .cancel-button:focus {
        background: #ffffff !important;
        border-color: #1f243b !important;
        color: #1f243b !important;
        border-radius: 8px !important;
        font-family: 'DM Sans', sans-serif;
      }

      .save-draft-button,
      .save-draft-button:hover,
      .save-draft-button:focus {
        background: #ffffff !important;
        border-color: #1f243b !important;
        color: #1f243b !important;
        border-radius: 8px !important;
        font-family: 'DM Sans', sans-serif;
      }

      .ant-notification-notice.timesheet-toast {
        text-align: center;
        border-radius: 12px;
        width: 220px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .ant-notification-notice.timesheet-toast .ant-notification-notice-message,
      .ant-notification-notice.timesheet-toast .ant-notification-notice-content {
        margin: 0;
      }
      .ant-notification-notice.timesheet-toast .ant-notification-notice-message {
        padding-right: 0;
      }
      .ant-notification-notice.timesheet-toast .ant-notification-notice-close {
        display: none;
      }
      .ts-toast {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        justify-content: center;
      }
      .ts-toast-icon {
        font-size: 32px;
        color: #02b209;
      }
      .ts-toast-text {
        font-size: 15px;
        font-weight: 600;
        color: #1b1b1b;
      }
      .ant-notification-notice-icon{
          position: relative !important;
      }
      .weekly-summary-popover .ant-popover-inner-content {
        padding: 0;
      }
      .weekly-summary-popover .ant-popover-inner {
        box-shadow: none;
        background: transparent;
      }
      .weekly-summary-popover .ant-popover-arrow {
        display: none;
      }
    `}
      </style>

      <MyTimeSheetLayout>
        <Spin spinning={isLoading || isCopying}>
          {/* {timeSheetStatus === ONGOING ? (
            <div className="d-inline-flex justify-content-center align-items-center gap-2 mb-2">
              <span className="duplicate-week">Duplicate Week for Cloning: </span>
              <span>
                <div className="position-relative">
                  <ConfigProvider locale={en_GB}>
                    <DatePicker
                      picker="week"
                      value={copyWeekTimesheet}
                      onChange={handlePasteTimesheet}
                      disabledDate={disabledDate}
                      placeholder="Select"
                    />
                  </ConfigProvider>
                </div>
              </span>
            </div>
          ) : null} */}

          <TimeSheet
            selectedTabId={selectedTabId}
            timesheetgetdata={timesheetDataRes?.response?.response}
            editable={editableTimesheet}
            editables={true}
            data={timeSheet}
            onSubmit={onSubmit}
            showClaims={false}
            weekNavNode={weekNavNode}
            listMetaNode={
              <div className="list-meta-row">
                <span className="total-list">
                  Total List: {timeSheet.length}
                </span>
                <InstructionsTrigger className="instructions-link" />
              </div>
            }
            extraActions={{
              handlePersonInCharge,
              handleClearTimesheet,
              handleSaveTimesheet,
              handleAddMoreClaim,
              handleClearClaim,
              handleResetClaim,
              handleRemoveClaim,
              handleWithdraw: handleWithdrawTimesheet,
              handleResubmitDeclined,
              isResubmitting,
            }}
            week={week}
            holidays={holidays}
            leaves={leaves}
          />

          {editableTimesheet && (
            <div className="d-flex justify-content-end align-items-center gap-2 mt-4">
              <Button className="add-more" onClick={handleAddMore}>
                + Add
              </Button>
            </div>
          )}
        </Spin>

        <div className="footer-spacer" ref={footerAnchorRef} style={!editableTimesheet ? { height: 0 } : undefined} />
        <div
          className={`footer-row d-flex justify-content-between align-items-center${editableTimesheet ? " footer-row-editable" : " footer-row-static"}`}
          style={editableTimesheet ? { left: footerLeft, right: footerRight } : undefined}
        >
          <WeeklySummaryBackdrop $visible={isSummaryOpen || isToastVisible} />

          <Popover
            content={
              <TimeSheetOverView
                timeSheet={
                  liveTimeSheetValues
                    ? Object.values(liveTimeSheetValues).map((liveItem, idx) => ({
                      ...timeSheet[idx],
                      ...liveItem,
                    }))
                    : timeSheet
                }
              />
            }
            trigger="hover"
            placement="topLeft"
            overlayClassName="weekly-summary-popover"
            onVisibleChange={setIsSummaryOpen}
            zIndex={1050}
          >
            <span className="weekly-summary-trigger">
              <InfoCircleFilled /> Weekly Summary
            </span>
          </Popover>

          <div className="d-flex align-items-center gap-4">
            {editableTimesheet && !isLoading && !isFetching && !isCopying && (
              <>
                <StyledButtonCancel className="cancel-button" onClick={() => onCancel?.()}>
                  Cancel
                </StyledButtonCancel>

                <StyledButtonBack
                  className="save-draft-button"
                  onClick={handleSaveTimesheet}
                  loading={isUpdating}
                >
                  Save as Draft
                </StyledButtonBack>

                <Button
                  type="primary"
                  onClick={handleSubmitTimesheet}
                  loading={isSubmitting || isUpdating || loadingActualSummary}
                  className="submit-button"
                >
                  Submit
                </Button>
              </>
            )}
          </div>
        </div>
      </MyTimeSheetLayout>
    </Form>
  );
};

const WeeklySummaryBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const MyTimeSheetLayout = styled.div`
  margin-top: 20px;

  .list-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    font-family: "DM Sans", sans-serif;
    font-size: 16px;
    color: #1f243b;

    .total-list {
      font-weight: 400;
    }

    .instructions-link {
      font-family: "DM Sans", sans-serif;
      font-size: 18px;
      font-weight: 400;
      color: #0066ff;
    }
  }

  .footer-spacer {
    height: 80px;
  }

  .footer-row {
    flex-wrap: wrap;
    gap: 12px;
    position: fixed;
    bottom: 0;
    z-index: 100;
    padding: 16px 25px;
  }

  .footer-row-editable {
    background: #ffffff;
    border-top: 1px solid #e2e2e8;
    box-shadow: 0px -4px 12px rgba(0, 0, 0, 0.08);
  }

  .footer-row-static {
    position: static;
    padding: 30px 0;
  }

  .weekly-summary-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1f243b;
    font-family: "DM Sans", sans-serif;
    font-size: 18px;
    cursor: pointer;

    .anticon {
      color: #0066ff;
    }
  }
`;

const mapStateToProps = (state) => ({
  actualSummaryData: getActualSummaryData(state),
});

export default connect(mapStateToProps, null)(MyTimeSheet);